import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import type { CustomFieldSchemaResponse, CustomFieldType } from "@/api/types";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * Inline editor for `Record<string, unknown>` `custom_field_values` driven
 * by a list of `CustomFieldSchema`. Renders inputs by `field_type`:
 *   text → Input, number → Input[number], date → Input[date],
 *   dropdown → <select> with options.choices[], boolean → <input[checkbox]>.
 */
export interface CustomFieldsFormProps {
  schemas: CustomFieldSchemaResponse[];
  values: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  errors?: Record<string, string>;
  idPrefix?: string;
}

function stringValue(v: unknown): string {
  if (v === undefined || v === null) return "";
  return String(v);
}

function boolValue(v: unknown): boolean {
  if (typeof v === "boolean") return v;
  if (v === "true") return true;
  return false;
}

function readChoices(options: Record<string, unknown> | null): string[] {
  if (options === null) return [];
  const raw = options.choices;
  if (!Array.isArray(raw)) return [];
  return raw.filter((x): x is string => typeof x === "string");
}

function renderInput(
  schema: CustomFieldSchemaResponse,
  value: unknown,
  setValue: (v: unknown) => void,
  inputId: string,
): ReactElement {
  const ft: CustomFieldType = schema.field_type;
  if (ft === "boolean") {
    return (
      <input
        id={inputId}
        type="checkbox"
        checked={boolValue(value)}
        onChange={(e) => setValue(e.target.checked)}
        data-testid={`cf-input-${schema.key}`}
      />
    );
  }
  if (ft === "dropdown") {
    const choices = readChoices(schema.options);
    return (
      <select
        id={inputId}
        className="h-9 w-full rounded-md border bg-background px-2 text-sm"
        value={stringValue(value)}
        onChange={(e) => setValue(e.target.value)}
        data-testid={`cf-input-${schema.key}`}
      >
        <option value="">—</option>
        {choices.map((c) => (
          <option key={c} value={c}>
            {c}
          </option>
        ))}
      </select>
    );
  }
  const type = ft === "number" ? "number" : ft === "date" ? "date" : "text";
  return (
    <Input
      id={inputId}
      type={type}
      value={stringValue(value)}
      onChange={(e) => {
        if (ft === "number") {
          setValue(e.target.value === "" ? "" : Number(e.target.value));
        } else {
          setValue(e.target.value);
        }
      }}
      data-testid={`cf-input-${schema.key}`}
    />
  );
}

export function CustomFieldsForm({
  schemas,
  values,
  onChange,
  errors,
  idPrefix = "cf",
}: CustomFieldsFormProps) {
  const { t } = useTranslation();
  if (schemas.length === 0) return null;
  return (
    <div className="space-y-3" data-testid="custom-fields-form">
      {schemas.map((s) => {
        const id = `${idPrefix}-${s.key}`;
        const err = errors?.[s.key];
        return (
          <div key={s.id}>
            <Label htmlFor={id}>
              {s.label}
              {s.is_required ? <span aria-hidden> *</span> : null}
            </Label>
            {renderInput(
              s,
              values[s.key],
              (v) => {
                onChange({ ...values, [s.key]: v });
              },
              id,
            )}
            {err !== undefined ? (
              <p className="text-xs text-destructive">
                {t("clients.errors.customFieldRequired", { label: s.label })}
              </p>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Validate required custom fields. Returns a map of key→i18n token. Empty
 * object means "valid".
 */
export function validateCustomFields(
  schemas: CustomFieldSchemaResponse[],
  values: Record<string, unknown>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const s of schemas) {
    if (!s.is_required) continue;
    const v = values[s.key];
    if (v === undefined || v === null || v === "") {
      out[s.key] = "required";
    }
  }
  return out;
}
